import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef
} from "react"
import { useSupabaseClient, useUser } from "@supabase/auth-helpers-react"
import "../assets/scss/account-settings-modal.scss"
import { X } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import Cookies from "js-cookie"
import useAnalytics from "../hooks/useAnalytics"
import { useTheme } from "../contexts/ThemeContext"
import { deleteAccount } from "../api"
import { WEB_LOGIN_PATH } from "../routes/authPaths"

export type AccountSettingsModalHandle = {
  open: () => void
}

const AccountSettingsModal = forwardRef<AccountSettingsModalHandle>((_, ref) => {
  const supabase = useSupabaseClient()
  const user = useUser()
  const navigate = useNavigate()
  const { trackAccountSettingsClose, trackAccountSettingsSave, trackConfirmationAlertClick, trackAccountSettingsLogout } = useAnalytics()
  const { clearWithTheme } = useTheme()

  const [isOpen, setIsOpen] = useState(false)
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [initials, setInitials] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [originalFullName, setOriginalFullName] = useState("")

  const modalRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => ({
    open: async () => {
      if (!user) return

      const { data: profile } = await supabase
        .from("users")
        .select("*")
        .eq("id", user.id)
        .single()

      const fullName = profile?.full_name || ""
      const [first = "", ...rest] = fullName.trim().split(" ")
      const last = rest.join(" ")

      setFirstName(first)
      setLastName(last)
      setOriginalFullName(fullName)
      setEmail(profile?.email || user.email || "")
      setInitials((fullName || "??").slice(0, 2).toUpperCase())
      setIsOpen(true)
    },
  }))

  const close = () => {
    trackAccountSettingsClose()
    setIsOpen(false)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        close()
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  const handleDeleteAccount = async () => {
    if (!user) return

    // Show confirmation dialog using custom JSX
    toast.custom(() => (
      <div style={{ 
        width: '420px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        border: '1px solid #e5e5e5'
      }}>
        <div style={{ marginBottom: '12px' }}>
          <h3 style={{ 
            margin: '0 0 8px 0', 
            fontSize: '16px', 
            fontWeight: '600',
            color: '#161514'
          }}>
            Are you sure you want to delete your account?
          </h3>
          <p style={{ 
            margin: 0, 
            fontSize: '14px', 
            color: '#666',
            lineHeight: '1.4'
          }}>
            ⚠️ This action cannot be undone. All your data will be permanently deleted.
          </p>
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          gap: '8px',
          marginTop: '16px'
        }}>
          <button
            onClick={() => {
              toast.dismiss()
            }}
            style={{
              backgroundColor: '#f5f5f5',
              color: '#666',
              border: '1px solid #e5e5e5',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e5e5'
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5'
            }}
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              toast.dismiss()
              setIsDeleting(true)
              
              try {
                // Call the delete account API
                const result = await deleteAccount()
                
                // API call was successful
                toast.success("Account deleted successfully. We're taking care of removing all your data.")
                
                // Clear all data and redirect
                const cookies = Object.keys(Cookies.get())
                cookies.forEach(cookie => Cookies.remove(cookie))
                
                sessionStorage.clear()
                clearWithTheme()
                
                // Close modal and redirect
                close()
                navigate(WEB_LOGIN_PATH)
                
              } catch (error: any) {
                console.error('Delete account error:', error)
                toast.error(error?.message || 'Failed to delete account')
              } finally {
                setIsDeleting(false)
              }
            }}
            style={{
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#b91c1c'
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#dc2626'
            }}
          >
            Yes, Delete Account
          </button>
        </div>
      </div>
    ), {
      duration: 15000
    })
  }

  const handleSave = async () => {
    if (!user) return
    setIsSaving(true)

    const fullName = `${firstName.trim()}${lastName.trim() ? " " + lastName.trim() : ""}`
    const nameChanged = fullName !== originalFullName

    try {
      const { error: updateError } = await supabase
        .from("users")
        .update({
          full_name: fullName,
          email,
        })
        .eq("id", user.id)

      if (updateError) {
        console.error("Update error:", updateError)
        toast.error("Error saving changes")
        return
      }

      if (nameChanged) trackAccountSettingsSave('name', true)

      toast.success("Changes saved!")
      close()
    } finally {
      setIsSaving(false)
    }
  }

  const handleSignOut = async () => {
    trackAccountSettingsLogout()

    toast.message("Do you really want to log out?", {
      duration: 10000,
      style: { width: 370 },
      action: {
        label: "Yes",
        onClick: async () => {
          trackConfirmationAlertClick('logout', true)

          const cookies = Object.keys(Cookies.get())
          cookies.forEach(cookie => Cookies.remove(cookie))

          sessionStorage.clear()
          clearWithTheme()
          await supabase.auth.signOut()
          navigate(WEB_LOGIN_PATH)
        },
      },
      cancel: {
        label: "No",
        onClick: () => {
          trackConfirmationAlertClick('logout', false)
        },
      },
    })
  }

  if (!isOpen) return null

  return (
    <div className="account-modal">
      <div className="account-modal__container" ref={modalRef}>
        <div className="account-modal__header">
          <h2 className="account-modal__title">Account settings</h2>
          <button className="account-modal__close" onClick={close} aria-label="Close account settings">
            <X size={20} />
          </button>
        </div>

        <div className="account-modal__content">
          <div className="account-modal__form-group">
            <label className="account-modal__label">First Name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="account-modal__input"
            />
          </div>

          <div className="account-modal__form-group">
            <label className="account-modal__label">Last Name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="account-modal__input"
            />
          </div>

          <div className="account-modal__form-group">
            <label htmlFor="email" className="account-modal__label">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="account-modal__input"
              disabled={true}
            />
          </div>

          <div className="account-modal__form-group">
            <label className="account-modal__label">Avatar</label>
            <div className="account-modal__avatar-section">
              <div className="account-modal__avatar-preview">
                <div className="account-modal__avatar-initials">{initials}</div>
              </div>
              <div className="account-modal__avatar-info">
                <p className="account-modal__avatar-label">Profile picture</p>
              </div>
            </div>
          </div>

          <div className="account-modal__account-info">
            <div>
              <p className="account-modal__account-status">Active account</p>
              <p className="account-modal__account-name">Signed in as {`${firstName} ${lastName}`}</p>
            </div>
            <button className="account-modal__sign-out" onClick={handleSignOut} aria-label="Sign out of account">
              Sign out
            </button>
          </div>
        </div>

        <div className="account-modal__footer">
            <button 
              className="account-modal__delete-account" 
              onClick={handleDeleteAccount}
              disabled={isDeleting || isSaving}
              aria-label="Delete account"
            >
              {isDeleting ? "Deleting..." : "Delete account"}
            </button>
          <button className="account-modal__save" onClick={handleSave} disabled={isSaving || isDeleting} aria-label="Save account changes">
            Save changes
          </button>
        </div>
      </div>
    </div>
  )
})

export default AccountSettingsModal
